/**
 * Content Tracker super admin console (admin.quixomarketing.com/content-admin). Owner: ui-agency-admin.
 * Pages: agency list, the super admin list (the "Super admins" tab), one agency (tabs), one deliverable. Only active
 * super admins get past the server gate (GET /v1/content/admin/me); anyone else sees one "not a super admin" notice.
 * Every write opens a confirm step that needs a reason (min 5 characters) and shows the server's 409 / 400 as it is.
 * Pure helpers are exported for the tests; the screens themselves use the kit (brand tokens only).
 */
import { Fragment, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, Outlet, useNavigate, useParams, useSearchParams } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { APPROVALS, CATALOG_KINDS, CLIENT_KINDS, MEMBER_ROLES, PLATFORM_STATUSES, type ClientKind, type MemberRole, type PlatformStatus } from '@qm/contracts/content';
import { contentAdminApi, type ActivityEntry, type Agency, type AgencyClient, type AgencyCatalogRow, type AgencyMember, type AgencyRow, type AdminDeliverable, type ClientOverride, type DeliverableDetail, type DeliverableOverride, type OverrideFields, type SuperAdminRow } from '../api/content-admin';
import { errorDetails, errorInfo, seg } from '../kit/http';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, Checkbox, DataTable, EmptyState, ErrorNote, Field, Loadable, Notice, ReasonForm, Screen, Tabs, When, type Column } from '../kit/ui';
import type { Translate } from '@qm/i18n';
import './content-admin.css';

// ---------------------------------------------------------------- constants and pure helpers

export const TABS = ['deliverables', 'clients', 'team', 'catalog', 'scope', 'settings', 'history'] as const;
export type TabId = (typeof TABS)[number];
export const DELETED_MODES = ['include', 'only', 'exclude'] as const;
export type DeletedMode = (typeof DELETED_MODES)[number];
export const ACTIVITY_ENTITIES = ['deliverable', 'platform', 'client', 'member', 'catalog', 'scope', 'settings'] as const;
export const PARTNER_STATUSES = ['pending', 'approved', 'rejected', 'suspended'] as const;
/** Activity fields the history shows with a translated label (column names, as the server writes them). */
export const ACTIVITY_FIELDS = [
  'title', 'content_type', 'client_id', 'original_date', 'plan_month', 'rescheduled_date', 'stage', 'assignee_member_id', 'actual_post_date',
  'delivered_on', 'delivery_link', 'approval', 'remarks', 'deleted_at', 'status', 'post_url', 'platforms', 'kind', 'name', 'am_member_id',
  'active', 'role', 'email', 'user_id', 'label', 'is_early', 'is_ready', 'sort', 'quantity', 'at_risk_days', 'digest_hour',
] as const;
const REVERTIBLE_ENTITIES: ReadonlySet<string> = new Set(['deliverable', 'platform', 'client']);
const KNOWN_FIELDS: ReadonlySet<string> = new Set(ACTIVITY_FIELDS);

const REASON_KEY: Record<string, string> = {
  already_deleted: 'portal.admin.contentAdmin.err.alreadyDeleted',
  not_deleted: 'portal.admin.contentAdmin.err.notDeleted',
  not_revertible: 'portal.admin.contentAdmin.err.notRevertible',
  duplicate_name: 'portal.admin.contentAdmin.err.duplicateName',
  duplicate_user: 'portal.admin.contentAdmin.err.duplicateUser',
  version_conflict: 'portal.admin.contentAdmin.err.versionConflict',
  already_active: 'portal.admin.contentAdmin.err.alreadyActive',
  already_deactivated: 'portal.admin.contentAdmin.err.alreadyDeactivated',
  self_deactivation: 'portal.admin.contentAdmin.err.selfDeactivation',
  last_superadmin: 'portal.admin.contentAdmin.err.lastSuperAdmin',
};

/** Which message a failed write shows. The server reason wins; then the HTTP status. */
export function writeErrorKey(info: { status: number; code: string; reason: string | null }): string {
  if (info.reason && REASON_KEY[info.reason]) return REASON_KEY[info.reason]!;
  if (info.status === 404) return 'portal.kit.notFound';
  if (info.status === 403) return 'portal.kit.forbidden';
  if (info.status === 400 || info.code === 'validation_failed') return 'portal.admin.contentAdmin.err.validation';
  if (info.status === 409 || info.code === 'conflict') return 'portal.kit.conflict';
  return 'portal.kit.error';
}

export type GateOutcome = 'loading' | 'denied' | 'error' | 'ready';
/** 403 from /me means "signed in, but not one of the two super admins": the only outcome that shows the notice. */
export function gateOutcome(r: { status: 'loading' | 'ready' | 'error'; error?: unknown }): GateOutcome {
  if (r.status === 'loading') return 'loading';
  if (r.status === 'error') return errorInfo(r.error).status === 403 ? 'denied' : 'error';
  return 'ready';
}

export const deliverablesKey = (partnerId: string, f: { deleted: DeletedMode; clientId: string; q: string }) => `contentAdmin:deliverables:${partnerId}:${f.deleted}:${f.clientId}:${f.q}`;
export const agencyKey = (partnerId: string) => `contentAdmin:agency:${partnerId}`;
export const activityKey = (partnerId: string, entity: string) => `contentAdmin:activity:${partnerId}:${entity}`;
export const itemKey = (id: string) => `contentAdmin:item:${id}`;
export const AGENCIES_KEY = 'contentAdmin:agencies';
export const SUPERADMINS_KEY = 'contentAdmin:superadmins';

export const isLocalDate = (v: string) => /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(v);
export const isMonth = (v: string) => /^\d{4}-(0[1-9]|1[0-2])$/.test(v);
export const isHttps = (v: string) => /^https:\/\/\S+$/i.test(v);
/** Whole number inside [min, max], or null. */
export function intIn(v: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(v.trim())) return null;
  const n = Number(v.trim());
  return n >= min && n <= max ? n : null;
}

// ---- deliverable form

/** Deliverable fields the override form edits, in form order. */
export const ITEM_FIELDS = ['title', 'contentType', 'clientId', 'originalDate', 'planMonth', 'rescheduledDate', 'stage', 'assigneeId', 'actualPostDate', 'deliveredOn', 'deliveryLink', 'approval', 'remarks'] as const;
export type ItemField = (typeof ITEM_FIELDS)[number];
export type ItemDraft = Record<ItemField, string>;
export type PlatformChoice = 'keep' | 'remove' | PlatformStatus;
/** Server column (activity field name) of each form field. */
export const COLUMN_OF: Record<ItemField, string> = {
  title: 'title', contentType: 'content_type', clientId: 'client_id', originalDate: 'original_date', planMonth: 'plan_month', rescheduledDate: 'rescheduled_date',
  stage: 'stage', assigneeId: 'assignee_member_id', actualPostDate: 'actual_post_date', deliveredOn: 'delivered_on', deliveryLink: 'delivery_link', approval: 'approval', remarks: 'remarks',
};
const NULLABLE: ReadonlySet<ItemField> = new Set<ItemField>(['originalDate', 'planMonth', 'rescheduledDate', 'stage', 'assigneeId', 'actualPostDate', 'deliveredOn', 'deliveryLink', 'approval', 'remarks']);

export function draftOf(d: AdminDeliverable): ItemDraft {
  return {
    title: d.title, contentType: d.contentType, clientId: d.clientId, originalDate: d.originalDate ?? '', planMonth: d.planMonth ?? '',
    rescheduledDate: d.rescheduledDate ?? '', stage: d.stage ?? '', assigneeId: d.assigneeId ?? '', actualPostDate: d.actualPostDate ?? '',
    deliveredOn: d.deliveredOn ?? '', deliveryLink: d.deliveryLink ?? '', approval: d.approval ?? '', remarks: d.remarks ?? '',
  };
}

/** Fields that cannot be saved as they stand (the submit stays disabled). */
export function itemProblems(d: ItemDraft): ItemField[] {
  const bad: ItemField[] = [];
  if (!d.title.trim() || d.title.trim().length > 200) bad.push('title');
  if (!d.contentType.trim()) bad.push('contentType');
  if (!d.clientId.trim()) bad.push('clientId');
  for (const f of ['originalDate', 'planMonth', 'rescheduledDate', 'actualPostDate', 'deliveredOn'] as const) {
    const v = d[f].trim();
    if (v && (f === 'planMonth' ? !isMonth(v) : !isLocalDate(v))) bad.push(f);
  }
  if (d.deliveryLink.trim() && !isHttps(d.deliveryLink.trim())) bad.push('deliveryLink');
  if (d.remarks.length > 2000) bad.push('remarks');
  return bad;
}

export interface ItemPatch {
  fields: OverrideFields;
  changedFields: ItemField[];
  platforms: Record<string, { status: PlatformStatus | null }>;
  changedPlatforms: string[];
}

/** Only what differs from the stored item is sent. An empty nullable field means null (Unlock = clear). */
export function itemPatch(orig: ItemDraft, draft: ItemDraft, choices: Record<string, PlatformChoice>, current: Record<string, PlatformStatus>): ItemPatch {
  const fields: Record<string, unknown> = {};
  const changedFields: ItemField[] = [];
  for (const f of ITEM_FIELDS) {
    const before = orig[f].trim(); const after = draft[f].trim();
    if (before === after) continue;
    changedFields.push(f);
    fields[f] = NULLABLE.has(f) && after === '' ? null : after;
  }
  const platforms: Record<string, { status: PlatformStatus | null }> = {};
  for (const [p, choice] of Object.entries(choices)) {
    if (choice === 'keep') continue;
    if (choice === 'remove') { if (current[p]) platforms[p] = { status: null }; continue; }
    if (current[p] !== choice) platforms[p] = { status: choice };
  }
  return { fields: fields as OverrideFields, changedFields, platforms, changedPlatforms: Object.keys(platforms) };
}

// ---- clients, members, catalog, scope, settings

export interface ClientDraft { name: string; kind: ClientKind; accountManagerId: string; platforms: string[] }
export function clientProblems(d: ClientDraft): boolean {
  return d.name.trim().length < 2 || d.name.trim().length > 120 || !d.accountManagerId || d.platforms.length > 20;
}
export function clientPatch(orig: AgencyClient, d: ClientDraft): Omit<ClientOverride, 'reason'> {
  const out: Omit<ClientOverride, 'reason'> = {};
  if (d.name.trim() !== orig.name) out.name = d.name.trim();
  if (d.kind !== orig.kind) out.kind = d.kind;
  if ((orig.accountManagerId ?? '') !== d.accountManagerId) out.accountManagerId = d.accountManagerId;
  if ([...orig.platforms].sort().join(',') !== [...d.platforms].sort().join(',')) out.platforms = [...d.platforms];
  return out;
}

export interface MemberDraft { name: string; role: MemberRole; email: string }
export const memberEmailOk = (v: string) => v.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
export function memberProblems(d: MemberDraft): boolean {
  return d.name.trim().length < 1 || d.name.trim().length > 120 || !memberEmailOk(d.email);
}
export function memberPatch(orig: AgencyMember, d: MemberDraft): { name?: string; role?: MemberRole; email?: string | null } {
  const out: { name?: string; role?: MemberRole; email?: string | null } = {};
  if (d.name.trim() !== orig.name) out.name = d.name.trim();
  if (d.role !== orig.role) out.role = d.role;
  const email = d.email.trim().toLowerCase() || null;
  if (email !== (orig.email ?? null)) out.email = email;
  return out;
}

export interface CatalogDraft { label: string; active: boolean; isEarly: boolean; isReady: boolean }
export function catalogPatch(orig: AgencyCatalogRow, d: CatalogDraft): { label?: string; active?: boolean; isEarly?: boolean; isReady?: boolean } {
  const out: { label?: string; active?: boolean; isEarly?: boolean; isReady?: boolean } = {};
  if (d.label.trim() !== orig.label) out.label = d.label.trim();
  if (d.active !== orig.active) out.active = d.active;
  if (orig.kind === 'stage') {
    if (d.isEarly !== orig.isEarly) out.isEarly = d.isEarly;
    if (d.isReady !== orig.isReady) out.isReady = d.isReady;
  }
  return out;
}

/** Scope quantities that were typed: a valid whole number from 0 to 10000 per content type. */
export function scopeItems(qty: Record<string, string>): { items: { contentType: string; quantity: number }[]; invalid: string[] } {
  const items: { contentType: string; quantity: number }[] = []; const invalid: string[] = [];
  for (const [contentType, raw] of Object.entries(qty)) {
    if (!raw || !raw.trim()) continue;
    const n = intIn(raw, 0, 10000);
    if (n === null) invalid.push(contentType); else items.push({ contentType, quantity: n });
  }
  return { items, invalid };
}

export interface SettingsDraft { atRiskDays: string; digestHour: string }
/** Only the settings that changed are sent; an invalid value makes the whole form invalid. */
export function settingsPatch(orig: { atRiskDays: number; digestHour: number }, d: SettingsDraft): { atRiskDays?: number; digestHour?: number } | null {
  const a = intIn(d.atRiskDays, 0, 30); const h = intIn(d.digestHour, 0, 23);
  if (a === null || h === null) return null;
  const out: { atRiskDays?: number; digestHour?: number } = {};
  if (a !== orig.atRiskDays) out.atRiskDays = a;
  if (h !== orig.digestHour) out.digestHour = h;
  return out;
}

/** Activity values are stored as text; these entities/fields get a human label or a lookup. */
export function revertable(a: Pick<ActivityEntry, 'entity'>): boolean {
  return REVERTIBLE_ENTITIES.has(a.entity);
}

export const statusKey = (s: string) => `portal.admin.contentAdmin.status.${s}`;

export function catalogLabel(agency: Agency | null, kind: string, key: string | null | undefined): string {
  if (!key) return '';
  return agency?.catalog.find((c) => c.kind === kind && c.key === key)?.label ?? key;
}
export function memberName(agency: Agency | null, id: string | null | undefined): string {
  if (!id) return '';
  return agency?.members.find((m) => m.id === id)?.name ?? id;
}
export function clientName(agency: Agency | null, id: string | null | undefined): string {
  if (!id) return '';
  return agency?.clients.find((c) => c.id === id)?.name ?? id;
}

/** A value as the history and the confirm step show it. */
export function valueText(t: Translate, agency: Agency | null, field: string, value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return t('portal.admin.contentAdmin.none');
  switch (field) {
    case 'client_id': return clientName(agency, value);
    case 'am_member_id': case 'assignee_member_id': return memberName(agency, value);
    case 'content_type': return catalogLabel(agency, 'content_type', value);
    case 'stage': return catalogLabel(agency, 'stage', value);
    case 'status': return (PLATFORM_STATUSES as readonly string[]).includes(value) ? t(`portal.admin.contentAdmin.platformStatus.${value}`) : value;
    case 'approval': return (APPROVALS as readonly string[]).includes(value) ? t(`portal.admin.contentAdmin.approval.${value}`) : value;
    case 'kind': return (CLIENT_KINDS as readonly string[]).includes(value) ? t(`portal.admin.contentAdmin.clientKind.${value}`) : value;
    case 'role': return (MEMBER_ROLES as readonly string[]).includes(value) ? t(`portal.admin.contentAdmin.memberRole.${value}`) : value;
    case 'platforms': return value.split(',').filter(Boolean).map((p) => catalogLabel(agency, 'platform', p)).join(', ');
    case 'active': case 'is_early': case 'is_ready': return t(value === '1' || value === 'true' ? 'portal.admin.contentAdmin.yes' : 'portal.admin.contentAdmin.no');
    case 'deleted_at': return t(value ? 'portal.admin.contentAdmin.yes' : 'portal.admin.contentAdmin.no');
    default: return value;
  }
}

export function fieldText(t: Translate, field: string): string {
  return KNOWN_FIELDS.has(field) ? t(`portal.admin.contentAdmin.field.${field}`) : field;
}

export interface Change { label: string; from: string; to: string }

// ---------------------------------------------------------------- data

function useApi() {
  const { http } = useSurface();
  return useMemo(() => contentAdminApi(http), [http]);
}

const sortCatalog = (a: AgencyCatalogRow, b: AgencyCatalogRow) => CATALOG_KINDS.indexOf(a.kind) - CATALOG_KINDS.indexOf(b.kind) || a.sort - b.sort || a.key.localeCompare(b.key);

// ---------------------------------------------------------------- shared write pieces

/** A write confirm step: the change list, a required reason, the server's answer. Closes itself on success. */
export function ConfirmWrite({ changes, submitLabel, run, onDone, onCancel, testId }: {
  changes: Change[]; submitLabel: string; run: (reason: string) => Promise<unknown>; onDone: () => void; onCancel: () => void; testId?: string;
}) {
  const { t } = useSurface();
  const act = useAction(run);
  const ref = useRef<HTMLElement>(null);
  const headingId = useId();
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <section ref={ref} tabIndex={-1} className="qca-confirm" aria-labelledby={headingId} data-testid={testId}>
      <Heading level={3} id={headingId}>{t('portal.admin.contentAdmin.confirm.title')}</Heading>
      <ChangeList changes={changes} />
      <ReasonForm label={t('portal.kit.reason')} submitLabel={submitLabel} pending={act.pending}
        onSubmit={async (reason) => { if ((await act.run(reason)) !== null) onDone(); }} />
      <div><Button onClick={onCancel}>{t('portal.admin.contentAdmin.cancel')}</Button></div>
      {act.error ? <WriteError error={act.error} /> : null}
    </section>
  );
}

export function ChangeList({ changes }: { changes: Change[] }) {
  const { t } = useSurface();
  if (changes.length === 0) return <Text tone="muted">{t('portal.admin.contentAdmin.confirm.none')}</Text>;
  return (
    <ul className="qca-changes" aria-label={t('portal.admin.contentAdmin.confirm.changes')}>
      {changes.map((c, i) => (
        <li key={`${c.label}-${i}`}>
          <span className="qca-change__label">{c.label}</span>
          <span className="qca-mono">{c.from}</span>
          <span aria-hidden="true">→</span>
          <span className="qca-mono">{c.to}</span>
        </li>
      ))}
    </ul>
  );
}

/** A failed write, shown as the server said it: a clear message for the reason or status, the rejected fields, the reference. */
export function WriteError({ error }: { error: unknown }) {
  const { t } = useSurface();
  const info = errorInfo(error);
  const details = errorDetails(error);
  const issues = Array.isArray(details) ? (details as { path?: unknown; message?: unknown }[]) : [];
  return (
    <div className="qx-alert" role="alert" data-code={info.code} data-status={info.status}>
      <Text>{t(writeErrorKey(info))}</Text>
      {issues.length > 0 ? (
        <ul className="qca-issues">
          {issues.map((i, n) => <li key={n} className="qca-mono">{Array.isArray(i.path) ? i.path.join('.') : t('portal.admin.contentAdmin.err.field')}</li>)}
        </ul>
      ) : null}
      <Text tone="muted">{t('portal.admin.contentAdmin.err.detail', { status: info.status, code: info.code })}</Text>
      {info.correlationId ? <Text tone="muted">{t('portal.kit.reference', { id: info.correlationId })}</Text> : null}
    </div>
  );
}

/** Activity history: every entry with its change, reason and author; a Revert button per revertible row. */
export function ActivityTable({ rows, agency, onReverted, showItem }: {
  rows: readonly ActivityEntry[]; agency: Agency | null; onReverted: () => void; showItem?: boolean;
}) {
  const { t } = useSurface();
  const api = useApi();
  const [reverting, setReverting] = useState<string | null>(null);
  if (rows.length === 0) return <EmptyState message={t('portal.admin.contentAdmin.history.empty')} />;
  const cols = showItem ? 8 : 7;
  return (
    <div className="qx-table-wrap">
      <table className="qx-table qca-activity">
        <caption className="qx-sr">{t('portal.admin.contentAdmin.history.caption')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('portal.admin.contentAdmin.history.when')}</th>
            <th scope="col">{t('portal.admin.contentAdmin.history.entity')}</th>
            <th scope="col">{t('portal.admin.contentAdmin.history.field')}</th>
            <th scope="col">{t('portal.admin.contentAdmin.history.change')}</th>
            <th scope="col">{t('portal.admin.contentAdmin.history.reason')}</th>
            <th scope="col">{t('portal.admin.contentAdmin.history.by')}</th>
            {showItem ? <th scope="col">{t('portal.admin.contentAdmin.history.item')}</th> : null}
            <th scope="col">{t('portal.admin.contentAdmin.history.action')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const label = fieldText(t, r.field);
            const entityLabel = (ACTIVITY_ENTITIES as readonly string[]).includes(r.entity) ? t(`portal.admin.contentAdmin.entity.${r.entity}`) : r.entity;
            return (
              <Fragment key={r.id}>
                <tr data-row={r.id}>
                  <td data-label={t('portal.admin.contentAdmin.history.when')}><When iso={r.at} /></td>
                  <td data-label={t('portal.admin.contentAdmin.history.entity')}>{entityLabel}</td>
                  <td data-label={t('portal.admin.contentAdmin.history.field')}>{label}</td>
                  <td data-label={t('portal.admin.contentAdmin.history.change')}>
                    <span className="qca-mono">{valueText(t, agency, r.field, r.oldValue)}</span>{' → '}<span className="qca-mono">{valueText(t, agency, r.field, r.newValue)}</span>
                  </td>
                  <td data-label={t('portal.admin.contentAdmin.history.reason')}>{r.reason ?? ''}</td>
                  <td data-label={t('portal.admin.contentAdmin.history.by')} className="qca-mono">{r.userId}</td>
                  {showItem ? (
                    <td data-label={t('portal.admin.contentAdmin.history.item')}>
                      {r.deliverableId ? <Link to={`/content-admin/items/${seg(r.deliverableId)}`}>{t('portal.admin.contentAdmin.openItem')}</Link> : null}
                    </td>
                  ) : null}
                  <td data-label={t('portal.admin.contentAdmin.history.action')}>
                    {revertable(r) ? (
                      <Button size="sm" aria-expanded={reverting === r.id} aria-label={t('portal.admin.contentAdmin.revertNamed', { field: label, id: r.id })}
                        onClick={() => setReverting(reverting === r.id ? null : r.id)}>{t('portal.admin.contentAdmin.revert')}</Button>
                    ) : <Text tone="muted">{t('portal.admin.contentAdmin.notRevertible')}</Text>}
                  </td>
                </tr>
                {reverting === r.id ? (
                  <tr key={`${r.id}-revert`} className="qca-revert-row">
                    <td colSpan={cols}>
                      <ConfirmWrite testId="revert-confirm" submitLabel={t('portal.admin.contentAdmin.revertSubmit')}
                        changes={[{ label, from: valueText(t, agency, r.field, r.newValue), to: valueText(t, agency, r.field, r.oldValue) }]}
                        run={(reason) => api.revertActivity(r.id, reason)}
                        onDone={() => { setReverting(null); onReverted(); }} onCancel={() => setReverting(null)} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- gate and notices

export function NotSuperAdmin() {
  const { t } = useSurface();
  return <Notice tone="warn" testId="content-admin-denied">{t('portal.admin.contentAdmin.notSuperAdmin')}</Notice>;
}

/** Layout route for every content-admin page: one /me check, then the pages. A 403 shows only the notice. */
export function ContentAdminRoot() {
  const api = useApi();
  const { t } = useSurface();
  const me = useResource('contentAdmin:me', api.me);
  const outcome = gateOutcome(me);
  if (outcome === 'loading') return <Text tone="muted" role="status">{t('portal.kit.loading')}</Text>;
  if (outcome === 'denied') return <NotSuperAdmin />;
  if (me.status === 'error') return <div><ErrorNote error={me.error} /><Button onClick={me.reload}>{t('portal.kit.retry')}</Button></div>;
  return <Outlet />;
}

// ---------------------------------------------------------------- agency list

export function ContentAgenciesPage() {
  const api = useApi();
  const { t } = useSurface();
  const list = useResource(AGENCIES_KEY, api.agencies);
  return (
    <Screen testId="content-admin" eyebrow={t('portal.admin.contentAdmin.eyebrow')} title={t('portal.admin.contentAdmin.title')} subtitle={t('portal.admin.contentAdmin.subtitle')}>
      <ConsoleTabs active="agencies" />
      <Loadable resource={list}>{(rows) => <AgencyTable rows={rows} />}</Loadable>
    </Screen>
  );
}

export function AgencyTable({ rows }: { rows: readonly AgencyRow[] }) {
  const { t } = useSurface();
  const columns: Column<AgencyRow>[] = [
    { key: 'name', header: t('portal.admin.contentAdmin.agency.name'), render: (r) => <Link to={`/content-admin/${seg(r.partnerId)}`}>{r.companyName}</Link> },
    { key: 'id', header: t('portal.admin.contentAdmin.agency.id'), narrow: 'hide', render: (r) => <span className="qca-mono">{r.partnerId}</span> },
    { key: 'status', header: t('portal.admin.status'), render: (r) => <Badge>{(PARTNER_STATUSES as readonly string[]).includes(r.status) ? t(`portal.admin.contentAdmin.partnerStatus.${r.status}`) : r.status}</Badge> },
    { key: 'clients', header: t('portal.admin.contentAdmin.agency.clients'), render: (r) => r.clients },
    { key: 'deliverables', header: t('portal.admin.contentAdmin.agency.deliverables'), render: (r) => r.deliverables },
    { key: 'deleted', header: t('portal.admin.contentAdmin.agency.deleted'), render: (r) => (r.deleted > 0 ? <Badge tone="warn">{r.deleted}</Badge> : 0) },
  ];
  return <DataTable columns={columns} rows={rows} rowKey={(r) => r.partnerId} empty={t('portal.admin.contentAdmin.agency.empty')} caption={t('portal.admin.contentAdmin.agency.caption')} />;
}

// ---------------------------------------------------------------- super admin list

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Lower-cased, trimmed: the form of the address the server stores and compares. */
export const normaliseEmail = (v: string) => v.trim().toLowerCase();
/** True when the typed address cannot be sent (empty, too long or not an address). */
export function superAdminEmailProblem(v: string): boolean {
  const e = normaliseEmail(v);
  return e.length === 0 || e.length > 254 || !EMAIL_SHAPE.test(e);
}
/** What a row offers. The caller's own row never offers a deactivate (the server refuses it anyway). */
export function superAdminAction(row: Pick<SuperAdminRow, 'active' | 'self'>): 'deactivate' | 'reactivate' | null {
  if (row.self) return null;
  return row.active ? 'deactivate' : 'reactivate';
}

/** The console's top-level sections. Agencies and Super admins are separate pages; this strip moves between them. */
export function ConsoleTabs({ active }: { active: 'agencies' | 'superadmins' }) {
  const { t } = useSurface();
  const navigate = useNavigate();
  return (
    <Tabs label={t('portal.admin.contentAdmin.console.tabs')} active={active}
      onSelect={(id) => navigate(id === 'superadmins' ? '/content-admin/superadmins' : '/content-admin')}
      tabs={[
        { id: 'agencies', label: t('portal.admin.contentAdmin.console.agencies') },
        { id: 'superadmins', label: t('portal.admin.contentAdmin.console.superadmins') },
      ]} />
  );
}

/** The super admin list: add (staff email), deactivate and reactivate, each behind the reason confirm step. */
export function ContentSuperAdminsPage() {
  const api = useApi();
  const { t } = useSurface();
  const list = useResource(SUPERADMINS_KEY, api.superadmins);
  const [email, setEmail] = useState('');
  const [adding, setAdding] = useState<string | null>(null);
  const [pending, setPending] = useState<SuperAdminRow | null>(null);
  const problem = superAdminEmailProblem(email);
  const columns: Column<SuperAdminRow>[] = [
    { key: 'email', header: t('portal.admin.contentAdmin.superadmin.email'), render: (r) => (
      <><span className="qca-mono">{r.email}</span>{r.self ? <> <Badge tone="good" testId={`self-${r.email}`}>{t('portal.admin.contentAdmin.superadmin.you')}</Badge></> : null}</>
    ) },
    { key: 'status', header: t('portal.admin.contentAdmin.superadmin.status'), render: (r) => (
      <Badge tone={r.active ? 'good' : 'warn'} testId={`status-${r.email}`}>{t(r.active ? 'portal.admin.contentAdmin.active' : 'portal.admin.contentAdmin.superadmin.deactivated')}</Badge>
    ) },
    { key: 'addedBy', header: t('portal.admin.contentAdmin.superadmin.addedBy'), narrow: 'hide', render: (r) => <span className="qca-mono">{r.addedBy}</span> },
    { key: 'addedAt', header: t('portal.admin.contentAdmin.superadmin.addedAt'), narrow: 'hide', render: (r) => <When iso={r.createdAt} /> },
    { key: 'action', header: t('portal.admin.contentAdmin.superadmin.actions'), render: (r) => {
      const action = superAdminAction(r);
      if (action === null) return <Text tone="muted">{t('portal.admin.contentAdmin.superadmin.selfNote')}</Text>;
      return (
        <Button size="sm" aria-pressed={pending?.email === r.email} data-testid={`${action}-${r.email}`}
          aria-label={t(action === 'deactivate' ? 'portal.admin.contentAdmin.superadmin.deactivateNamed' : 'portal.admin.contentAdmin.superadmin.reactivateNamed', { email: r.email })}
          onClick={() => setPending(r)}>
          {t(action === 'deactivate' ? 'portal.admin.contentAdmin.superadmin.deactivate' : 'portal.admin.contentAdmin.superadmin.reactivate')}
        </Button>
      );
    } },
  ];
  return (
    <Screen testId="content-superadmins" eyebrow={t('portal.admin.contentAdmin.eyebrow')} title={t('portal.admin.contentAdmin.title')}
      subtitle={t('portal.admin.contentAdmin.superadmin.intro')}>
      <ConsoleTabs active="superadmins" />
      <Loadable resource={list}>{(rows) => (
        <div className="qca-stack">
          <DataTable columns={columns} rows={rows} rowKey={(r) => r.email} empty={t('portal.admin.contentAdmin.superadmin.empty')}
            caption={t('portal.admin.contentAdmin.superadmin.caption')} />
          {pending ? (
            <ConfirmWrite testId="superadmin-state-confirm" submitLabel={t('portal.admin.contentAdmin.confirm.submit')}
              changes={[{ label: pending.email, from: t(pending.active ? 'portal.admin.contentAdmin.active' : 'portal.admin.contentAdmin.superadmin.deactivated'), to: t(pending.active ? 'portal.admin.contentAdmin.superadmin.deactivated' : 'portal.admin.contentAdmin.active') }]}
              run={(reason) => (pending.active ? api.deactivateSuperAdmin(pending.email, reason) : api.reactivateSuperAdmin(pending.email, reason))}
              onDone={() => { setPending(null); list.reload(); }} onCancel={() => setPending(null)} />
          ) : null}
          <Panel aria-label={t('portal.admin.contentAdmin.superadmin.addTitle')}>
            <Heading level={2}>{t('portal.admin.contentAdmin.superadmin.addTitle')}</Heading>
            <form className="qca-editor" onSubmit={(e) => { e.preventDefault(); if (!problem) setAdding(normaliseEmail(email)); }}>
              <Field label={t('portal.admin.contentAdmin.superadmin.emailLabel')} hint={t('portal.admin.contentAdmin.superadmin.addHint')}
                error={email.trim() && problem ? t('portal.admin.contentAdmin.superadmin.emailInvalid') : null}>
                {(id, d) => <input id={id} type="email" value={email} maxLength={254} autoComplete="off" aria-describedby={d} onChange={(e) => { setEmail(e.target.value); setAdding(null); }} />}
              </Field>
              <div className="qca-editor__actions">
                <Button type="submit" variant="accent" disabled={problem}>{t('portal.admin.contentAdmin.review')}</Button>
              </div>
            </form>
            {adding ? (
              <ConfirmWrite testId="superadmin-add-confirm" submitLabel={t('portal.admin.contentAdmin.confirm.submit')}
                changes={[{ label: t('portal.admin.contentAdmin.superadmin.email'), from: t('portal.admin.contentAdmin.none'), to: adding }]}
                run={(reason) => api.addSuperAdmin({ reason, email: adding })}
                onDone={() => { setAdding(null); setEmail(''); list.reload(); }} onCancel={() => setAdding(null)} />
            ) : null}
          </Panel>
        </div>
      )}</Loadable>
    </Screen>
  );
}

// ---------------------------------------------------------------- one agency

export function ContentAgencyPage() {
  const { partnerId = '' } = useParams();
  const api = useApi();
  const { t } = useSurface();
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab') ?? '';
  const active: TabId = (TABS as readonly string[]).includes(requested) ? (requested as TabId) : 'deliverables';
  const agency = useResource(agencyKey(partnerId), () => api.agency(partnerId));
  const agencies = useResource(AGENCIES_KEY, api.agencies);
  const name = agencies.status === 'ready' ? agencies.data.find((r) => r.partnerId === partnerId)?.companyName ?? partnerId : partnerId;
  return (
    <Screen testId="content-agency" eyebrow={t('portal.admin.contentAdmin.eyebrow')} title={name} subtitle={partnerId}
      actions={<Link to="/content-admin">{t('portal.admin.contentAdmin.backToAgencies')}</Link>}>
      <Loadable resource={agency}>{(a) => (
        <Tabs label={t('portal.admin.contentAdmin.tabs')} active={active} onSelect={(id) => setParams({ tab: id }, { replace: true })}
          tabs={TABS.map((id) => ({ id, label: t(`portal.admin.contentAdmin.tab.${id}`) }))}>
          <AgencyPanel tab={active} agency={a} onChanged={agency.reload} />
        </Tabs>
      )}</Loadable>
    </Screen>
  );
}

function AgencyPanel({ tab, agency, onChanged }: { tab: TabId; agency: Agency; onChanged: () => void }) {
  switch (tab) {
    case 'deliverables': return <DeliverablesTab agency={agency} />;
    case 'clients': return <ClientsTab agency={agency} onChanged={onChanged} />;
    case 'team': return <TeamTab agency={agency} onChanged={onChanged} />;
    case 'catalog': return <CatalogTab agency={agency} onChanged={onChanged} />;
    case 'scope': return <ScopeTab agency={agency} />;
    case 'settings': return <SettingsTab agency={agency} onChanged={onChanged} />;
    case 'history': return <HistoryTab agency={agency} />;
  }
}

function DeliverablesTab({ agency }: { agency: Agency }) {
  const api = useApi();
  const { t } = useSurface();
  const partnerId = agency.partnerId;
  const [deleted, setDeleted] = useState<DeletedMode>('include');
  const [clientId, setClientId] = useState('');
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');
  const filters = { deleted, clientId, q };
  const list = useResource(deliverablesKey(partnerId, filters), () => api.deliverables(partnerId, {
    deleted, ...(clientId ? { clientId } : {}), ...(q ? { q } : {}),
  }));
  const columns: Column<AdminDeliverable>[] = [
    { key: 'title', header: t('portal.admin.contentAdmin.deliverable.title'), render: (r) => (
      <><Link to={`/content-admin/items/${seg(r.id)}`}>{r.title}</Link>{r.deletedAt ? <> <Badge tone="warn" testId={`deleted-${r.id}`}>{t('portal.admin.contentAdmin.deleted')}</Badge></> : null}</>
    ) },
    { key: 'client', header: t('portal.admin.contentAdmin.deliverable.client'), render: (r) => r.clientName },
    { key: 'type', header: t('portal.admin.contentAdmin.deliverable.type'), narrow: 'hide', render: (r) => catalogLabel(agency, 'content_type', r.contentType) },
    { key: 'stage', header: t('portal.admin.contentAdmin.deliverable.stage'), narrow: 'hide', render: (r) => catalogLabel(agency, 'stage', r.stage) || t('portal.admin.contentAdmin.none') },
    { key: 'status', header: t('portal.admin.contentAdmin.deliverable.status'), render: (r) => t(statusKey(r.status)) },
    { key: 'date', header: t('portal.admin.contentAdmin.deliverable.workingDate'), narrow: 'hide', render: (r) => <span className="qca-mono">{r.workingDate ?? t('portal.admin.contentAdmin.none')}</span> },
  ];
  return (
    <div className="qca-stack">
      <form className="qx-toolbar" role="search" onSubmit={(e) => { e.preventDefault(); setQ(draft.trim()); }}>
        <Field label={t('portal.admin.contentAdmin.filter.deleted')}>{(id) => (
          <select id={id} value={deleted} onChange={(e) => setDeleted(e.target.value as DeletedMode)}>
            {DELETED_MODES.map((m) => <option key={m} value={m}>{t(`portal.admin.contentAdmin.deleted.${m}`)}</option>)}
          </select>
        )}</Field>
        <Field label={t('portal.admin.contentAdmin.filter.client')}>{(id) => (
          <select id={id} value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">{t('portal.admin.contentAdmin.filter.allClients')}</option>
            {agency.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}</Field>
        <Field label={t('portal.admin.contentAdmin.filter.search')}>{(id) => (
          <input id={id} type="search" value={draft} maxLength={100} onChange={(e) => setDraft(e.target.value)} />
        )}</Field>
        <Button type="submit">{t('portal.admin.contentAdmin.filter.apply')}</Button>
      </form>
      <Loadable resource={list}>{(rows) => (
        <>
          <Text tone="muted" role="status">{t('portal.admin.contentAdmin.deliverable.count', { n: rows.length })}</Text>
          <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} empty={t('portal.admin.contentAdmin.deliverable.empty')}
            caption={t('portal.admin.contentAdmin.deliverable.caption')} rowClassName={(r) => (r.deletedAt ? 'qca-row--deleted' : undefined)} />
        </>
      )}</Loadable>
    </div>
  );
}

/** Pick one row, edit it below the table, and confirm with a reason. Only one editor is open at a time. */
function ClientsTab({ agency, onChanged }: { agency: Agency; onChanged: () => void }) {
  const { t } = useSurface();
  const [editing, setEditing] = useState<string | null>(null);
  const client = agency.clients.find((c) => c.id === editing) ?? null;
  const columns: Column<AgencyClient>[] = [
    { key: 'name', header: t('portal.admin.contentAdmin.client.name'), render: (r) => r.name },
    { key: 'kind', header: t('portal.admin.contentAdmin.client.kind'), render: (r) => t(`portal.admin.contentAdmin.clientKind.${r.kind}`) },
    { key: 'am', header: t('portal.admin.contentAdmin.client.am'), narrow: 'hide', render: (r) => memberName(agency, r.accountManagerId) },
    { key: 'platforms', header: t('portal.admin.contentAdmin.client.platforms'), narrow: 'hide', render: (r) => r.platforms.map((p) => catalogLabel(agency, 'platform', p)).join(', ') || t('portal.admin.contentAdmin.none') },
    { key: 'state', header: t('portal.admin.contentAdmin.client.state'), render: (r) => <Badge tone={r.active ? 'good' : 'warn'}>{t(r.active ? 'portal.admin.contentAdmin.active' : 'portal.admin.contentAdmin.archived')}</Badge> },
    { key: 'edit', header: '', render: (r) => (
      <Button size="sm" aria-pressed={editing === r.id} aria-label={t('portal.admin.contentAdmin.editNamed', { name: r.name })} onClick={() => setEditing(r.id)}>{t('portal.admin.contentAdmin.edit')}</Button>
    ) },
  ];
  return (
    <div className="qca-stack">
      <DataTable columns={columns} rows={agency.clients} rowKey={(r) => r.id} empty={t('portal.admin.contentAdmin.client.empty')} caption={t('portal.admin.contentAdmin.client.caption')} />
      {client ? <ClientEditor key={client.id} agency={agency} client={client} onChanged={onChanged} /> : null}
    </div>
  );
}

function ClientEditor({ agency, client, onChanged }: { agency: Agency; client: AgencyClient; onChanged: () => void }) {
  const api = useApi();
  const { t } = useSurface();
  const [name, setName] = useState(client.name);
  const [kind, setKind] = useState<ClientKind>(client.kind);
  const [am, setAm] = useState(client.accountManagerId ?? '');
  const [platforms, setPlatforms] = useState<string[]>(client.platforms);
  const [mode, setMode] = useState<null | 'save' | 'toggle'>(null);
  const draft: ClientDraft = { name, kind, accountManagerId: am, platforms };
  const patch = clientPatch(client, draft);
  const changed = Object.keys(patch).length > 0;
  const platformKeys = [...new Set([...agency.catalog.filter((c) => c.kind === 'platform').map((c) => c.key), ...client.platforms])];
  const changes: Change[] = [
    ...(patch.name !== undefined ? [{ label: t('portal.admin.contentAdmin.client.name'), from: client.name, to: patch.name }] : []),
    ...(patch.kind !== undefined ? [{ label: t('portal.admin.contentAdmin.client.kind'), from: valueText(t, agency, 'kind', client.kind), to: valueText(t, agency, 'kind', patch.kind) }] : []),
    ...(patch.accountManagerId !== undefined ? [{ label: t('portal.admin.contentAdmin.client.am'), from: memberName(agency, client.accountManagerId), to: memberName(agency, patch.accountManagerId) }] : []),
    ...(patch.platforms !== undefined ? [{ label: t('portal.admin.contentAdmin.client.platforms'), from: valueText(t, agency, 'platforms', client.platforms.join(',')), to: valueText(t, agency, 'platforms', patch.platforms.join(',')) }] : []),
  ];
  const toggleChange: Change[] = [{ label: t('portal.admin.contentAdmin.client.state'), from: valueText(t, agency, 'active', client.active ? '1' : '0'), to: valueText(t, agency, 'active', client.active ? '0' : '1') }];
  return (
    <Panel className="qca-editor-panel" aria-label={t('portal.admin.contentAdmin.editing', { name: client.name })}>
      <Heading level={2}>{client.name}</Heading>
      <form className="qca-editor" onSubmit={(e) => { e.preventDefault(); if (changed && !clientProblems(draft)) setMode('save'); }}>
        <Field label={t('portal.admin.contentAdmin.client.name')}>{(id) => <input id={id} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />}</Field>
        <Field label={t('portal.admin.contentAdmin.client.kind')}>{(id) => (
          <select id={id} value={kind} onChange={(e) => setKind(e.target.value as ClientKind)}>
            {CLIENT_KINDS.map((k) => <option key={k} value={k}>{t(`portal.admin.contentAdmin.clientKind.${k}`)}</option>)}
          </select>
        )}</Field>
        <Field label={t('portal.admin.contentAdmin.client.am')}>{(id) => (
          <select id={id} value={am} onChange={(e) => setAm(e.target.value)}>
            {agency.members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}</Field>
        <fieldset className="qca-wide qca-fieldset">
          <legend>{t('portal.admin.contentAdmin.client.platforms')}</legend>
          <div className="qca-platforms">
            {platformKeys.map((p) => (
              <Checkbox key={p} name={`platform-${p}`} label={catalogLabel(agency, 'platform', p)} checked={platforms.includes(p)}
                onChange={(on) => setPlatforms(on ? [...platforms, p] : platforms.filter((x) => x !== p))} />
            ))}
          </div>
        </fieldset>
        <div className="qca-editor__actions">
          <Button type="submit" variant="accent" disabled={!changed || clientProblems(draft)}>{t('portal.admin.contentAdmin.review')}</Button>
          <Button onClick={() => setMode('toggle')}>{t(client.active ? 'portal.admin.contentAdmin.archive' : 'portal.admin.contentAdmin.restore')}</Button>
        </div>
      </form>
      {mode === 'save' ? (
        <ConfirmWrite testId="client-confirm" changes={changes} submitLabel={t('portal.admin.contentAdmin.confirm.submit')}
          run={(reason) => api.overrideClient(agency.partnerId, client.id, { reason, ...patch })}
          onDone={() => { setMode(null); onChanged(); }} onCancel={() => setMode(null)} />
      ) : null}
      {mode === 'toggle' ? (
        <ConfirmWrite testId="client-state-confirm" changes={toggleChange} submitLabel={t('portal.admin.contentAdmin.confirm.submit')}
          run={(reason) => api.overrideClient(agency.partnerId, client.id, { reason, active: !client.active })}
          onDone={() => { setMode(null); onChanged(); }} onCancel={() => setMode(null)} />
      ) : null}
    </Panel>
  );
}

function TeamTab({ agency, onChanged }: { agency: Agency; onChanged: () => void }) {
  const { t } = useSurface();
  const [editing, setEditing] = useState<string | null>(null);
  const member = agency.members.find((m) => m.id === editing) ?? null;
  const columns: Column<AgencyMember>[] = [
    { key: 'name', header: t('portal.admin.contentAdmin.member.name'), render: (r) => r.name },
    { key: 'role', header: t('portal.admin.contentAdmin.member.role'), render: (r) => t(`portal.admin.contentAdmin.memberRole.${r.role}`) },
    { key: 'email', header: t('portal.admin.contentAdmin.member.email'), narrow: 'hide', render: (r) => r.email ?? t('portal.admin.contentAdmin.none') },
    { key: 'state', header: t('portal.admin.contentAdmin.member.state'), render: (r) => <Badge tone={r.active ? 'good' : 'warn'}>{t(r.active ? 'portal.admin.contentAdmin.active' : 'portal.admin.contentAdmin.archived')}</Badge> },
    { key: 'edit', header: '', render: (r) => (
      <Button size="sm" aria-pressed={editing === r.id} aria-label={t('portal.admin.contentAdmin.editNamed', { name: r.name })} onClick={() => setEditing(r.id)}>{t('portal.admin.contentAdmin.edit')}</Button>
    ) },
  ];
  return (
    <div className="qca-stack">
      <DataTable columns={columns} rows={agency.members} rowKey={(r) => r.id} empty={t('portal.admin.contentAdmin.member.empty')} caption={t('portal.admin.contentAdmin.member.caption')} />
      {member ? <MemberEditor key={member.id} agency={agency} member={member} onChanged={onChanged} /> : null}
    </div>
  );
}

function MemberEditor({ agency, member, onChanged }: { agency: Agency; member: AgencyMember; onChanged: () => void }) {
  const api = useApi();
  const { t } = useSurface();
  const [name, setName] = useState(member.name);
  const [role, setRole] = useState<MemberRole>(member.role);
  const [email, setEmail] = useState(member.email ?? '');
  const [mode, setMode] = useState<null | 'save' | 'toggle'>(null);
  const draft: MemberDraft = { name, role, email };
  const patch = memberPatch(member, draft);
  const changed = Object.keys(patch).length > 0;
  const changes: Change[] = [
    ...(patch.name !== undefined ? [{ label: t('portal.admin.contentAdmin.member.name'), from: member.name, to: patch.name }] : []),
    ...(patch.role !== undefined ? [{ label: t('portal.admin.contentAdmin.member.role'), from: valueText(t, agency, 'role', member.role), to: valueText(t, agency, 'role', patch.role) }] : []),
    ...(patch.email !== undefined ? [{ label: t('portal.admin.contentAdmin.member.email'), from: member.email ?? '', to: patch.email ?? '' }] : []),
  ];
  const toggleChange: Change[] = [{ label: t('portal.admin.contentAdmin.member.state'), from: valueText(t, agency, 'active', member.active ? '1' : '0'), to: valueText(t, agency, 'active', member.active ? '0' : '1') }];
  return (
    <Panel aria-label={t('portal.admin.contentAdmin.editing', { name: member.name })}>
      <Heading level={2}>{member.name}</Heading>
      <form className="qca-editor" onSubmit={(e) => { e.preventDefault(); if (changed && !memberProblems(draft)) setMode('save'); }}>
        <Field label={t('portal.admin.contentAdmin.member.name')}>{(id) => <input id={id} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />}</Field>
        <Field label={t('portal.admin.contentAdmin.member.role')}>{(id) => (
          <select id={id} value={role} onChange={(e) => setRole(e.target.value as MemberRole)}>
            {MEMBER_ROLES.map((r) => <option key={r} value={r}>{t(`portal.admin.contentAdmin.memberRole.${r}`)}</option>)}
          </select>
        )}</Field>
        <Field label={t('portal.admin.contentAdmin.member.email')} hint={t('portal.admin.contentAdmin.member.emailHint')}
          error={memberEmailOk(email) ? null : t('portal.admin.contentAdmin.member.emailInvalid')}>
          {(id, describedBy) => <input id={id} type="email" value={email} maxLength={254} aria-describedby={describedBy} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <div className="qca-editor__actions">
          <Button type="submit" variant="accent" disabled={!changed || memberProblems(draft)}>{t('portal.admin.contentAdmin.review')}</Button>
          <Button onClick={() => setMode('toggle')}>{t(member.active ? 'portal.admin.contentAdmin.archive' : 'portal.admin.contentAdmin.restore')}</Button>
        </div>
      </form>
      {mode === 'save' ? (
        <ConfirmWrite testId="member-confirm" changes={changes} submitLabel={t('portal.admin.contentAdmin.confirm.submit')}
          run={(reason) => api.overrideMember(agency.partnerId, member.id, { reason, ...patch })}
          onDone={() => { setMode(null); onChanged(); }} onCancel={() => setMode(null)} />
      ) : null}
      {mode === 'toggle' ? (
        <ConfirmWrite testId="member-state-confirm" changes={toggleChange} submitLabel={t('portal.admin.contentAdmin.confirm.submit')}
          run={(reason) => api.overrideMember(agency.partnerId, member.id, { reason, active: !member.active })}
          onDone={() => { setMode(null); onChanged(); }} onCancel={() => setMode(null)} />
      ) : null}
    </Panel>
  );
}

function CatalogTab({ agency, onChanged }: { agency: Agency; onChanged: () => void }) {
  const { t } = useSurface();
  const [editing, setEditing] = useState<string | null>(null);
  const rows = [...agency.catalog].sort(sortCatalog);
  const item = rows.find((r) => r.id === editing) ?? null;
  const columns: Column<AgencyCatalogRow>[] = [
    { key: 'kind', header: t('portal.admin.contentAdmin.catalog.kind'), render: (r) => t(`portal.admin.contentAdmin.catalogKind.${r.kind}`) },
    { key: 'key', header: t('portal.admin.contentAdmin.catalog.key'), narrow: 'hide', render: (r) => <span className="qca-mono">{r.key}</span> },
    { key: 'label', header: t('portal.admin.contentAdmin.catalog.label'), render: (r) => r.label },
    { key: 'state', header: t('portal.admin.contentAdmin.catalog.state'), render: (r) => <Badge tone={r.active ? 'good' : 'warn'}>{t(r.active ? 'portal.admin.contentAdmin.active' : 'portal.admin.contentAdmin.inactive')}</Badge> },
    { key: 'flags', header: t('portal.admin.contentAdmin.catalog.flags'), narrow: 'hide', render: (r) => (r.kind === 'stage'
      ? [r.isEarly ? t('portal.admin.contentAdmin.catalog.early') : '', r.isReady ? t('portal.admin.contentAdmin.catalog.ready') : ''].filter(Boolean).join(', ') || t('portal.admin.contentAdmin.none')
      : t('portal.admin.contentAdmin.none')) },
    { key: 'edit', header: '', render: (r) => (
      <Button size="sm" aria-pressed={editing === r.id} aria-label={t('portal.admin.contentAdmin.editNamed', { name: r.label })} onClick={() => setEditing(r.id)}>{t('portal.admin.contentAdmin.edit')}</Button>
    ) },
  ];
  return (
    <div className="qca-stack">
      <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} empty={t('portal.admin.contentAdmin.catalog.empty')} caption={t('portal.admin.contentAdmin.catalog.caption')} />
      {item ? <CatalogEditor key={item.id} agency={agency} row={item} onChanged={onChanged} /> : null}
    </div>
  );
}

function CatalogEditor({ agency, row, onChanged }: { agency: Agency; row: AgencyCatalogRow; onChanged: () => void }) {
  const api = useApi();
  const { t } = useSurface();
  const [label, setLabel] = useState(row.label);
  const [active, setActive] = useState(row.active);
  const [isEarly, setEarly] = useState(row.isEarly);
  const [isReady, setReady] = useState(row.isReady);
  const [review, setReview] = useState(false);
  const draft: CatalogDraft = { label, active, isEarly, isReady };
  const patch = catalogPatch(row, draft);
  const changed = Object.keys(patch).length > 0;
  const valid = label.trim().length >= 1 && label.trim().length <= 60;
  const changes: Change[] = [
    ...(patch.label !== undefined ? [{ label: t('portal.admin.contentAdmin.catalog.label'), from: row.label, to: patch.label }] : []),
    ...(patch.active !== undefined ? [{ label: t('portal.admin.contentAdmin.catalog.state'), from: valueText(t, agency, 'active', row.active ? '1' : '0'), to: valueText(t, agency, 'active', patch.active ? '1' : '0') }] : []),
    ...(patch.isEarly !== undefined ? [{ label: t('portal.admin.contentAdmin.catalog.early'), from: valueText(t, agency, 'is_early', row.isEarly ? '1' : '0'), to: valueText(t, agency, 'is_early', patch.isEarly ? '1' : '0') }] : []),
    ...(patch.isReady !== undefined ? [{ label: t('portal.admin.contentAdmin.catalog.ready'), from: valueText(t, agency, 'is_ready', row.isReady ? '1' : '0'), to: valueText(t, agency, 'is_ready', patch.isReady ? '1' : '0') }] : []),
  ];
  return (
    <Panel aria-label={t('portal.admin.contentAdmin.editing', { name: row.label })}>
      <Heading level={2}>{row.label}</Heading>
      <form className="qca-editor" onSubmit={(e) => { e.preventDefault(); if (changed && valid) setReview(true); }}>
        <Field label={t('portal.admin.contentAdmin.catalog.label')}>{(id) => <input id={id} value={label} maxLength={60} onChange={(e) => setLabel(e.target.value)} />}</Field>
        <div className="qca-wide qca-checks">
          <Checkbox label={t('portal.admin.contentAdmin.catalog.active')} checked={active} onChange={setActive} />
          {row.kind === 'stage' ? <Checkbox label={t('portal.admin.contentAdmin.catalog.early')} checked={isEarly} onChange={setEarly} /> : null}
          {row.kind === 'stage' ? <Checkbox label={t('portal.admin.contentAdmin.catalog.ready')} checked={isReady} onChange={setReady} /> : null}
        </div>
        <div className="qca-editor__actions">
          <Button type="submit" variant="accent" disabled={!changed || !valid}>{t('portal.admin.contentAdmin.review')}</Button>
        </div>
      </form>
      {review ? (
        <ConfirmWrite testId="catalog-confirm" changes={changes} submitLabel={t('portal.admin.contentAdmin.confirm.submit')}
          run={(reason) => api.overrideCatalog(agency.partnerId, row.id, { reason, ...patch })}
          onDone={() => { setReview(false); onChanged(); }} onCancel={() => setReview(false)} />
      ) : null}
    </Panel>
  );
}

function ScopeTab({ agency }: { agency: Agency }) {
  const api = useApi();
  const { t, now } = useSurface();
  const types = agency.catalog.filter((c) => c.kind === 'content_type' && c.active).sort(sortCatalog);
  const [clientId, setClientId] = useState(agency.clients[0]?.id ?? '');
  const [month, setMonth] = useState(now().toISOString().slice(0, 7));
  const [qty, setQty] = useState<Record<string, string>>({});
  const [review, setReview] = useState(false);
  const { items, invalid } = scopeItems(qty);
  const client = agency.clients.find((c) => c.id === clientId) ?? null;
  const valid = !!client && isMonth(month) && items.length > 0 && invalid.length === 0;
  const changes: Change[] = items.map((i) => ({ label: catalogLabel(agency, 'content_type', i.contentType), from: t('portal.admin.contentAdmin.scope.notShown'), to: String(i.quantity) }));
  if (agency.clients.length === 0) return <EmptyState message={t('portal.admin.contentAdmin.client.empty')} />;
  return (
    <Panel aria-label={t('portal.admin.contentAdmin.scope.title')}>
      <Heading level={2}>{t('portal.admin.contentAdmin.scope.title')}</Heading>
      <Text tone="muted">{t('portal.admin.contentAdmin.scope.hint')}</Text>
      <form className="qca-editor" onSubmit={(e) => { e.preventDefault(); if (valid) setReview(true); }}>
        <Field label={t('portal.admin.contentAdmin.scope.client')}>{(id) => (
          <select id={id} value={clientId} onChange={(e) => { setClientId(e.target.value); setReview(false); }}>
            {agency.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}</Field>
        <Field label={t('portal.admin.contentAdmin.scope.month')} error={isMonth(month) ? null : t('portal.admin.contentAdmin.scope.monthInvalid')}>
          {(id, describedBy) => <input id={id} type="month" value={month} aria-describedby={describedBy} onChange={(e) => { setMonth(e.target.value); setReview(false); }} />}
        </Field>
        {types.length === 0 ? <EmptyState message={t('portal.admin.contentAdmin.catalog.empty')} /> : types.map((ct) => (
          <Field key={ct.key} label={t('portal.admin.contentAdmin.scope.quantity', { type: ct.label })}
            error={invalid.includes(ct.key) ? t('portal.admin.contentAdmin.scope.quantityInvalid') : null}>
            {(id, describedBy) => <input id={id} type="number" inputMode="numeric" min={0} max={10000} step={1} value={qty[ct.key] ?? ''} aria-describedby={describedBy}
              onChange={(e) => { setQty({ ...qty, [ct.key]: e.target.value }); setReview(false); }} />}
          </Field>
        ))}
        <div className="qca-editor__actions">
          <Button type="submit" variant="accent" disabled={!valid}>{t('portal.admin.contentAdmin.review')}</Button>
        </div>
      </form>
      {review && client ? (
        <ConfirmWrite testId="scope-confirm" changes={[{ label: t('portal.admin.contentAdmin.scope.client'), from: '', to: `${client.name} · ${month}` }, ...changes]}
          submitLabel={t('portal.admin.contentAdmin.confirm.submit')}
          run={(reason) => api.putScope(agency.partnerId, { reason, clientId: client.id, month, items })}
          onDone={() => { setReview(false); setQty({}); }} onCancel={() => setReview(false)} />
      ) : null}
    </Panel>
  );
}

function SettingsTab({ agency, onChanged }: { agency: Agency; onChanged: () => void }) {
  const api = useApi();
  const { t } = useSurface();
  const [days, setDays] = useState(String(agency.settings.atRiskDays));
  const [hour, setHour] = useState(String(agency.settings.digestHour));
  const [review, setReview] = useState(false);
  const patch = settingsPatch(agency.settings, { atRiskDays: days, digestHour: hour });
  const changed = patch !== null && Object.keys(patch).length > 0;
  const changes: Change[] = [
    ...(patch?.atRiskDays !== undefined ? [{ label: t('portal.admin.contentAdmin.settings.atRisk'), from: String(agency.settings.atRiskDays), to: String(patch.atRiskDays) }] : []),
    ...(patch?.digestHour !== undefined ? [{ label: t('portal.admin.contentAdmin.settings.digestHour'), from: String(agency.settings.digestHour), to: String(patch.digestHour) }] : []),
  ];
  return (
    <Panel aria-label={t('portal.admin.contentAdmin.settings.title')}>
      <Heading level={2}>{t('portal.admin.contentAdmin.settings.title')}</Heading>
      <form className="qca-editor" onSubmit={(e) => { e.preventDefault(); if (changed) setReview(true); }}>
        <Field label={t('portal.admin.contentAdmin.settings.atRisk')} hint={t('portal.admin.contentAdmin.settings.atRiskHint')}
          error={intIn(days, 0, 30) === null ? t('portal.admin.contentAdmin.settings.invalid', { min: 0, max: 30 }) : null}>
          {(id, describedBy) => <input id={id} type="number" inputMode="numeric" min={0} max={30} step={1} value={days} aria-describedby={describedBy} onChange={(e) => { setDays(e.target.value); setReview(false); }} />}
        </Field>
        <Field label={t('portal.admin.contentAdmin.settings.digestHour')} hint={t('portal.admin.contentAdmin.settings.digestHourHint')}
          error={intIn(hour, 0, 23) === null ? t('portal.admin.contentAdmin.settings.invalid', { min: 0, max: 23 }) : null}>
          {(id, describedBy) => <input id={id} type="number" inputMode="numeric" min={0} max={23} step={1} value={hour} aria-describedby={describedBy} onChange={(e) => { setHour(e.target.value); setReview(false); }} />}
        </Field>
        <div className="qca-editor__actions">
          <Button type="submit" variant="accent" disabled={!changed}>{t('portal.admin.contentAdmin.review')}</Button>
        </div>
      </form>
      {review && patch ? (
        <ConfirmWrite testId="settings-confirm" changes={changes} submitLabel={t('portal.admin.contentAdmin.confirm.submit')}
          run={(reason) => api.putSettings(agency.partnerId, { reason, ...patch })}
          onDone={() => { setReview(false); onChanged(); }} onCancel={() => setReview(false)} />
      ) : null}
    </Panel>
  );
}

function HistoryTab({ agency }: { agency: Agency }) {
  const api = useApi();
  const { t } = useSurface();
  const [entity, setEntity] = useState('');
  const list = useResource(activityKey(agency.partnerId, entity), () => api.activity(agency.partnerId, entity || undefined));
  return (
    <div className="qca-stack">
      <form className="qx-toolbar" role="search" onSubmit={(e) => e.preventDefault()}>
        <Field label={t('portal.admin.contentAdmin.history.filter')}>{(id) => (
          <select id={id} value={entity} onChange={(e) => setEntity(e.target.value)}>
            <option value="">{t('portal.admin.contentAdmin.filter.allEntities')}</option>
            {ACTIVITY_ENTITIES.map((x) => <option key={x} value={x}>{t(`portal.admin.contentAdmin.entity.${x}`)}</option>)}
          </select>
        )}</Field>
      </form>
      <Loadable resource={list}>{(rows) => <ActivityTable rows={rows} agency={agency} showItem onReverted={list.reload} />}</Loadable>
    </div>
  );
}

// ---------------------------------------------------------------- one deliverable

export function ContentItemPage() {
  const { id = '' } = useParams();
  const api = useApi();
  const detail = useResource(itemKey(id), () => api.deliverable(id));
  return (
    <Loadable resource={detail}>{(d) => <ItemView detail={d} onChanged={detail.reload} />}</Loadable>
  );
}

function ItemView({ detail, onChanged }: { detail: DeliverableDetail; onChanged: () => void }) {
  const api = useApi();
  const { t } = useSurface();
  const { item } = detail;
  const agency = useResource(agencyKey(item.partnerId), () => api.agency(item.partnerId));
  const [mode, setMode] = useState<null | 'restore' | 'delete'>(null);
  const fileColumns: Column<DeliverableDetail['files'][number]>[] = [
    { key: 'v', header: t('portal.admin.contentAdmin.item.version'), render: (f) => f.versionNo },
    { key: 'label', header: t('portal.admin.contentAdmin.item.fileLabel'), render: (f) => f.label ?? t('portal.admin.contentAdmin.none') },
    { key: 'when', header: t('portal.admin.contentAdmin.history.when'), render: (f) => <When iso={f.createdAt} /> },
  ];
  return (
    <Screen testId="content-item" eyebrow={t('portal.admin.contentAdmin.eyebrow')} title={item.title}
      subtitle={`${item.clientName} · ${item.id}`}
      actions={<Link to={`/content-admin/${seg(item.partnerId)}`}>{t('portal.admin.contentAdmin.backToAgency')}</Link>}>
      {item.deletedAt ? <Notice tone="warn" testId="item-deleted">{t('portal.admin.contentAdmin.item.deletedNotice')}</Notice> : null}
      <div className="qca-actions-row">
        {item.deletedAt
          ? <Button data-testid="item-restore" onClick={() => setMode('restore')}>{t('portal.admin.contentAdmin.item.restore')}</Button>
          : <Button variant="ghost" data-testid="item-delete" onClick={() => setMode('delete')}>{t('portal.admin.contentAdmin.item.delete')}</Button>}
      </div>
      {mode ? (
        <ConfirmWrite testId={`item-${mode}-confirm`} submitLabel={t('portal.admin.contentAdmin.confirm.submit')}
          changes={[{ label: t('portal.admin.contentAdmin.item.deletedState'), from: valueText(t, null, 'deleted_at', item.deletedAt ? 'x' : null), to: valueText(t, null, 'deleted_at', mode === 'delete' ? 'x' : null) }]}
          run={(reason) => (mode === 'delete' ? api.deleteDeliverable(item.id, reason) : api.restoreDeliverable(item.id, reason))}
          onDone={() => { setMode(null); onChanged(); }} onCancel={() => setMode(null)} />
      ) : null}
      <Loadable resource={agency}>{(a) => (
        <ItemEditor key={`${item.id}:${item.version}`} agency={a} item={item} onChanged={onChanged} />
      )}</Loadable>
      <Panel aria-label={t('portal.admin.contentAdmin.item.files')}>
        <Heading level={2}>{t('portal.admin.contentAdmin.item.files')}</Heading>
        <DataTable columns={fileColumns} rows={detail.files} rowKey={(f) => f.id} empty={t('portal.admin.contentAdmin.item.noFiles')} caption={t('portal.admin.contentAdmin.item.files')} />
      </Panel>
      <Panel aria-label={t('portal.admin.contentAdmin.item.activity')}>
        <Heading level={2}>{t('portal.admin.contentAdmin.item.activity')}</Heading>
        <Loadable resource={agency}>{(a) => <ActivityTable rows={detail.activity} agency={a} onReverted={onChanged} />}</Loadable>
      </Panel>
    </Screen>
  );
}

function ItemEditor({ agency, item, onChanged }: { agency: Agency; item: AdminDeliverable; onChanged: () => void }) {
  const api = useApi();
  const { t } = useSurface();
  const orig = draftOf(item);
  const [draft, setDraft] = useState<ItemDraft>(orig);
  const [choices, setChoices] = useState<Record<string, PlatformChoice>>({});
  const [review, setReview] = useState(false);
  const set = (f: ItemField, v: string) => { setDraft((d) => ({ ...d, [f]: v })); setReview(false); };
  const current: Record<string, PlatformStatus> = Object.fromEntries(item.platforms.map((p) => [p.platform, p.status]));
  const platformKeys = [...new Set([...item.platforms.map((p) => p.platform), ...(agency.clients.find((c) => c.id === item.clientId)?.platforms ?? [])])];
  const problems = itemProblems(draft);
  const patch = itemPatch(orig, draft, choices, current);
  const changedCount = patch.changedFields.length + patch.changedPlatforms.length;
  const valid = problems.length === 0 && changedCount > 0;
  const changes: Change[] = [
    ...patch.changedFields.map((f) => ({ label: fieldText(t, COLUMN_OF[f]), from: valueText(t, agency, COLUMN_OF[f], orig[f] || null), to: valueText(t, agency, COLUMN_OF[f], draft[f].trim() || null) })),
    ...patch.changedPlatforms.map((p) => ({ label: `${catalogLabel(agency, 'platform', p)} · ${fieldText(t, 'status')}`, from: valueText(t, agency, 'status', current[p] ?? null), to: valueText(t, agency, 'status', patch.platforms[p]?.status ?? null) })),
  ];
  const err = (f: ItemField) => (problems.includes(f) ? t(`portal.admin.contentAdmin.item.invalid.${f}`) : null);
  return (
    <Panel aria-label={t('portal.admin.contentAdmin.item.editor')}>
      <Heading level={2}>{t('portal.admin.contentAdmin.item.editor')}</Heading>
      <form className="qca-editor" onSubmit={(e) => { e.preventDefault(); if (valid) setReview(true); }}>
        <Field label={fieldText(t, 'title')} error={err('title')}>{(id, d) => <input id={id} value={draft.title} maxLength={200} aria-describedby={d} onChange={(e) => set('title', e.target.value)} />}</Field>
        <Field label={fieldText(t, 'content_type')} error={err('contentType')}>{(id) => (
          <select id={id} value={draft.contentType} onChange={(e) => set('contentType', e.target.value)}>
            {agency.catalog.filter((c) => c.kind === 'content_type' && (c.active || c.key === item.contentType)).sort(sortCatalog)
              .map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        )}</Field>
        <Field label={fieldText(t, 'client_id')} error={err('clientId')}>{(id) => (
          <select id={id} value={draft.clientId} onChange={(e) => set('clientId', e.target.value)}>
            {agency.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}</Field>
        <DateField label={fieldText(t, 'original_date')} value={draft.originalDate} error={err('originalDate')} onChange={(v) => set('originalDate', v)}
          extra={<Button size="sm" onClick={() => set('originalDate', '')} data-testid="unlock-original-date">{t('portal.admin.contentAdmin.item.unlock')}</Button>}
          hint={t('portal.admin.contentAdmin.item.unlockHint')} />
        <Field label={fieldText(t, 'plan_month')} error={err('planMonth')}>{(id, d) => <input id={id} type="month" value={draft.planMonth} aria-describedby={d} onChange={(e) => set('planMonth', e.target.value)} />}</Field>
        <DateField label={fieldText(t, 'rescheduled_date')} value={draft.rescheduledDate} error={err('rescheduledDate')} onChange={(v) => set('rescheduledDate', v)} />
        <Field label={fieldText(t, 'stage')}>{(id) => (
          <select id={id} value={draft.stage} onChange={(e) => set('stage', e.target.value)}>
            <option value="">{t('portal.admin.contentAdmin.none')}</option>
            {agency.catalog.filter((c) => c.kind === 'stage' && (c.active || c.key === item.stage)).sort(sortCatalog).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        )}</Field>
        <Field label={fieldText(t, 'assignee_member_id')}>{(id) => (
          <select id={id} value={draft.assigneeId} onChange={(e) => set('assigneeId', e.target.value)}>
            <option value="">{t('portal.admin.contentAdmin.none')}</option>
            {agency.members.filter((m) => m.active || m.id === item.assigneeId).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}</Field>
        <DateField label={fieldText(t, 'actual_post_date')} value={draft.actualPostDate} error={err('actualPostDate')} onChange={(v) => set('actualPostDate', v)} />
        <DateField label={fieldText(t, 'delivered_on')} value={draft.deliveredOn} error={err('deliveredOn')} onChange={(v) => set('deliveredOn', v)} />
        <Field label={fieldText(t, 'delivery_link')} hint={t('portal.admin.contentAdmin.item.httpsHint')} error={err('deliveryLink')}>
          {(id, d) => <input id={id} type="url" value={draft.deliveryLink} aria-describedby={d} onChange={(e) => set('deliveryLink', e.target.value)} />}
        </Field>
        <Field label={fieldText(t, 'approval')}>{(id) => (
          <select id={id} value={draft.approval} onChange={(e) => set('approval', e.target.value)}>
            <option value="">{t('portal.admin.contentAdmin.none')}</option>
            {APPROVALS.map((a) => <option key={a} value={a}>{t(`portal.admin.contentAdmin.approval.${a}`)}</option>)}
          </select>
        )}</Field>
        <Field label={fieldText(t, 'remarks')} error={err('remarks')}>{(id, d) => <textarea id={id} value={draft.remarks} maxLength={2000} aria-describedby={d} onChange={(e) => set('remarks', e.target.value)} />}</Field>
        {item.clientKind === 'posting' && platformKeys.length > 0 ? (
          <fieldset className="qca-wide qca-fieldset">
            <legend>{t('portal.admin.contentAdmin.item.platforms')}</legend>
            <p className="qx-sr" id="qca-platform-hint">{t('portal.admin.contentAdmin.item.platformsHint')}</p>
            <div className="qca-platform-rows">
              {platformKeys.map((p) => {
                const cur = current[p];
                return (
                  <Field key={p} label={`${catalogLabel(agency, 'platform', p)} (${cur ? valueText(t, agency, 'status', cur) : t('portal.admin.contentAdmin.item.notPlanned')})`}>
                    {(id, d) => (
                      <select id={id} aria-describedby={d ?? 'qca-platform-hint'} value={choices[p] ?? 'keep'} onChange={(e) => { setChoices({ ...choices, [p]: e.target.value as PlatformChoice }); setReview(false); }}>
                        <option value="keep">{t('portal.admin.contentAdmin.item.keep')}</option>
                        {cur ? <option value="remove">{t('portal.admin.contentAdmin.item.remove')}</option> : null}
                        {PLATFORM_STATUSES.map((s) => <option key={s} value={s}>{t(`portal.admin.contentAdmin.platformStatus.${s}`)}</option>)}
                      </select>
                    )}
                  </Field>
                );
              })}
            </div>
          </fieldset>
        ) : null}
        <div className="qca-editor__actions">
          <Button type="submit" variant="accent" disabled={!valid}>{t('portal.admin.contentAdmin.review')}</Button>
          <Text tone="muted">{t('portal.admin.contentAdmin.item.changeCount', { n: changedCount })}</Text>
        </div>
      </form>
      {review ? (
        <ConfirmWrite testId="item-confirm" changes={changes} submitLabel={t('portal.admin.contentAdmin.confirm.submit')}
          run={(reason) => {
            const body: DeliverableOverride = { reason, fields: patch.fields };
            if (patch.changedPlatforms.length) body.platforms = patch.platforms;
            return api.overrideDeliverable(item.id, body);
          }}
          onDone={() => { setReview(false); onChanged(); }} onCancel={() => setReview(false)} />
      ) : null}
    </Panel>
  );
}

/** Date input with an optional action button beside it and a hint / error that the input announces. */
function DateField({ label, value, onChange, error, hint, extra }: { label: string; value: string; onChange: (v: string) => void; error?: string | null; hint?: string; extra?: ReactNode }) {
  return (
    <Field label={label} hint={hint} error={error}>
      {(id, describedBy) => (
        <span className="qca-date">
          <input id={id} type="date" value={value} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)} />
          {extra}
        </span>
      )}
    </Field>
  );
}

